import type { ReportRun } from './api';

export type DisplayStatus = 'SUCCESS' | 'EMPTY' | 'FAILED';

export const DISPLAY_VARIANT: Record<DisplayStatus, 'success' | 'warning' | 'destructive'> = {
  SUCCESS: 'success',
  EMPTY: 'warning',
  FAILED: 'destructive',
};

export const DISPLAY_LABEL: Record<DisplayStatus, string> = {
  SUCCESS: 'SUCCESS',
  EMPTY: 'EMPTY',
  FAILED: 'FAILED',
};

export function deriveStatus(run: Pick<ReportRun, 'status' | 'row_count'>): DisplayStatus {
  if (run.status === 'FAILED') return 'FAILED';
  if ((run.row_count ?? 0) === 0) return 'EMPTY';
  return 'SUCCESS';
}

const MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

const DOW = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

/**
 * Humanizes the report-runner's pseudo-cron format: `<day-of-month> <month> <day-of-week>`.
 * Examples:
 *   "* * *"     → "Every day"
 *   "1 * *"     → "1st of every month"
 *   "* * 1-5"   → "Weekdays (Mon–Fri)"
 *   "* * 0,6"   → "Weekends"
 *   "15 1 *"    → "January 15"
 */
export function humanizeSchedule(schedule?: string): string {
  const raw = (schedule ?? '* * *').trim();
  if (raw === '' || raw === '* * *') return 'Every day';

  const parts = raw.split(/\s+/);
  if (parts.length !== 3) return raw;
  const [dom, month, dow] = parts as [string, string, string];

  if (dom === '*' && month === '*' && dow === '1-5') return 'Weekdays (Mon–Fri)';
  if (dom === '*' && month === '*' && (dow === '0,6' || dow === '6,0')) return 'Weekends';
  if (dom !== '*' && month === '*' && dow === '*') {
    const n = parseInt(dom, 10);
    return Number.isFinite(n) ? `${ordinal(n)} of every month` : `Day ${dom} of every month`;
  }
  if (dom === '*' && month !== '*' && dow === '*') {
    const n = parseInt(month, 10);
    return Number.isFinite(n) ? `Every day of ${MONTHS[n - 1] ?? month}` : raw;
  }
  if (dom !== '*' && month !== '*' && dow === '*') {
    const m = parseInt(month, 10);
    const d = parseInt(dom, 10);
    if (Number.isFinite(m) && Number.isFinite(d)) {
      return `${MONTHS[m - 1] ?? month} ${d}`;
    }
  }
  if (dom === '*' && month === '*' && dow !== '*') {
    return `On ${dowList(dow)}`;
  }

  return [
    dom === '*' ? null : `Day ${dom}`,
    month === '*' ? null : MONTHS[parseInt(month, 10) - 1] ?? month,
    dow === '*' ? null : dowList(dow),
  ]
    .filter(Boolean)
    .join(' · ');
}

function dowList(dow: string): string {
  if (dow.includes('-')) {
    const [aStr, bStr] = dow.split('-');
    const a = aStr === undefined ? NaN : parseInt(aStr, 10);
    const b = bStr === undefined ? NaN : parseInt(bStr, 10);
    if (Number.isFinite(a) && Number.isFinite(b)) {
      return `${DOW[a] ?? aStr}–${DOW[b] ?? bStr}`;
    }
  }
  if (dow.includes(',')) {
    return dow
      .split(',')
      .map((s) => DOW[parseInt(s, 10)] ?? s)
      .join(', ');
  }
  const n = parseInt(dow, 10);
  return Number.isFinite(n) ? DOW[n] ?? dow : dow;
}

function ordinal(n: number): string {
  const suffixes = ['th', 'st', 'nd', 'rd'] as const;
  const v = n % 100;
  const suffix = suffixes[(v - 20) % 10] ?? suffixes[v] ?? suffixes[0];
  return `${n}${suffix}`;
}
